/**
 * JWT auth helper — reads the same `authentik_secure_tokens` from localStorage
 * that the Vue-side apiClient uses, and returns headers suitable for fetch().
 */

export function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem("authentik_secure_tokens");
    if (!stored) return null;

    const tokenData = JSON.parse(stored);
    if (Date.now() >= tokenData.expiresAt) return null;

    const tokens = JSON.parse(atob(tokenData.tokens));
    return tokens.access_token || tokens.id_token;
  } catch {
    return null;
  }
}

export function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  if (!token) return { version: "2021-04-15", "source-id": "123" };
  return { Authorization: `Bearer ${token}`, version: "2021-04-15", "source-id": "123" };
}

export interface AuthUser {
  email: string | null;
  name: string | null;
}

/**
 * Decode the JWT payload to extract user info (email, name).
 * Does NOT validate the signature — that's the backend's job.
 */
export function getAuthUser(): AuthUser {
  const token = getAuthToken();
  if (!token) return { email: null, name: null };

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      email: payload.email ?? payload.preferred_username ?? null,
      name: payload.name ?? null,
    };
  } catch {
    return { email: null, name: null };
  }
}

/**
 * Format a user identity string from name and email.
 * Shows "Name (email)" when both exist, otherwise whichever is available.
 */
export function formatUserIdentity(name?: string | null, email?: string | null): string | null {
  if (name && email) return `${name} (${email})`;
  return name || email || null;
}
